/* Flags for the language picker: drawn as SVG because emoji flags don't render on Windows. Round by default;
   `square` fills a rounded square (the language modal's rows) */

type Props = {
  square?: boolean;
  size?: number;
};

const Flag = ({ id, square, size, children }: Props & { id: string; children: React.ReactNode }) => {
  const clipId = `flag-${id}${square ? "-square" : ""}`;
  const side = size ?? (square ? 40 : 24);
  return (
    <svg width={side} height={side} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <clipPath id={clipId}>
        {square ? <rect width="24" height="24" rx="6" /> : <circle cx="12" cy="12" r="12" />}
      </clipPath>
      <g clipPath={`url(#${clipId})`}>{children}</g>
    </svg>
  );
};

export const BrazilFlag = (props: Props) => (
  <Flag id="br" {...props}>
    <rect width="24" height="24" fill="#009C3B" />
    {props.square ? (
      <>
        <path d="M12 3.5 21.5 12 12 20.5 2.5 12Z" fill="#FFDF00" />
        <circle cx="12" cy="12" r="4.6" fill="#002776" />
        <path d="M7.5 11a8.5 8.5 0 0 1 9 2.2" stroke="#fff" strokeWidth="0.9" fill="none" />
      </>
    ) : (
      <>
        <path d="M12 4.5 22 12 12 19.5 2 12Z" fill="#FFDF00" />
        <circle cx="12" cy="12" r="4.5" fill="#002776" />
        <path d="M7.6 11.2a8 8 0 0 1 8.8 1.8" stroke="#fff" strokeWidth="0.9" fill="none" />
      </>
    )}
  </Flag>
);

export const UsaFlag = (props: Props) => (
  <Flag id="us" {...props}>
    <rect width="24" height="24" fill="#fff" />
    {[0, 2, 4, 6, 8, 10, 12].map(stripe => (
      <rect key={stripe} y={(stripe * 24) / 13} width="24" height={24 / 13} fill="#B22234" />
    ))}
    <rect width="11" height={(24 / 13) * 7} fill="#3C3B6E" />
    {[2.2, 5.5, 8.8].flatMap(x =>
      [2.2, 5.5, 8.8, 12].map(y => <circle key={`${x}-${y}`} cx={x} cy={y} r="0.7" fill="#fff" />)
    )}
  </Flag>
);

/* Three bands, vertical (France, Italy) or horizontal (Germany, Spain): weights are the band heights/widths */
const Bands = ({
  id,
  colors,
  weights,
  vertical,
  ...props
}: Props & {
  id: string;
  colors: string[];
  weights: number[];
  vertical?: boolean;
}) => {
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  let start = 0;
  return (
    <Flag id={id} {...props}>
      {colors.map((color, i) => {
        const size = (weights[i] * 24) / total;
        const band = vertical ? { x: start, width: size, height: 24 } : { y: start, width: 24, height: size };
        start += size;
        return <rect key={color + i} {...band} fill={color} />;
      })}
    </Flag>
  );
};

export const SpainFlag = (props: Props) => (
  <Bands id="es" colors={["#AA151B", "#F1BF00", "#AA151B"]} weights={[1, 2, 1]} {...props} />
);
export const FranceFlag = (props: Props) => (
  <Bands id="fr" colors={["#002654", "#fff", "#CE1126"]} weights={[1, 1, 1]} vertical {...props} />
);
export const GermanyFlag = (props: Props) => (
  <Bands id="de" colors={["#000", "#DD0000", "#FFCE00"]} weights={[1, 1, 1]} {...props} />
);
export const ItalyFlag = (props: Props) => (
  <Bands id="it" colors={["#009246", "#fff", "#CE2B37"]} weights={[1, 1, 1]} vertical {...props} />
);

/** Each editor language's flag (services/i18ln/languages). */
export const LANGUAGE_FLAGS = {
  pt: BrazilFlag,
  en: UsaFlag,
  es: SpainFlag,
  fr: FranceFlag,
  de: GermanyFlag,
  it: ItalyFlag,
};
